import React from 'react';
import { VulnerabilityInvestigation } from './VulnerabilityInvestigation';
import { safeJsonParse } from '../../../../../../utils/jsonRepairUtils';
import type { VulnerabilityInvestigationData } from './types';
import { ErrorBoundary } from '../ErrorBoundary';
import { useDebouncedValue } from '../useDebouncedValue';

interface MarkdownVulnerabilityInvestigationProps {
  code: string;
}

// Skeleton loader for loading state
const Skeleton = () => {
  return (
    <div className="my-4 overflow-hidden rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-900/5 transition-all duration-200 dark:bg-gray-900">
      <div className="animate-pulse">
        {/* Header skeleton */}
        <div className="mb-6 rounded-lg bg-gray-100 p-6 dark:bg-gray-800">
          <div className="mb-2 h-8 w-48 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-4 w-96 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="mt-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
            <div className="h-6 w-32 rounded bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>

        {/* Cards skeleton */}
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg bg-gray-100 p-4">
              <div className="mb-2 h-4 w-full rounded bg-gray-200 dark:bg-gray-700"></div>
              <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function isReportPayload(
  payload: unknown,
): payload is VulnerabilityInvestigationData {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }
  const candidate = payload as VulnerabilityInvestigationData;
  return Boolean(
    candidate.cveId ||
    candidate.assetId ||
    candidate.verdict ||
    candidate.operationalSeverity ||
    candidate.vulnerability ||
    candidate.asset ||
    (Array.isArray(candidate.evaluations) && candidate.evaluations.length >= 0),
  );
}

const InvestigationRenderer = ({ code }: { code: string }) => {
  // Use safeJsonParse which handles incomplete/streaming JSON gracefully
  const parsed = safeJsonParse<unknown>(
    code,
    'vulnerability investigation schema',
  );

  // If we can't parse anything at all, show skeleton
  if (!parsed) {
    return <Skeleton />;
  }

  if (isReportPayload(parsed)) {
    return <VulnerabilityInvestigation data={parsed} />;
  }

  return <Skeleton />;
};

/**
 * Markdown wrapper for VulnerabilityInvestigation widget system.
 * This component is used when rendering ```vulnerabilityInvestigation code blocks.
 */
export const MarkdownVulnerabilityInvestigation: React.FC<
  MarkdownVulnerabilityInvestigationProps
> = ({ code }) => {
  // Debounce the code to avoid re-rendering on every keystroke during streaming
  const debouncedCode = useDebouncedValue(code, 300, 500);

  return (
    <div className="not-prose my-4">
      <ErrorBoundary
        onError={(error, errorInfo) => {
          console.error(
            'Vulnerability investigation rendering error:',
            error,
            errorInfo,
            debouncedCode,
          );
        }}
        key={debouncedCode}
        fallback={<Skeleton />}
      >
        <InvestigationRenderer code={debouncedCode} />
      </ErrorBoundary>
    </div>
  );
};

export default MarkdownVulnerabilityInvestigation;
